import React, { useEffect, useRef, useState } from "react";
import { setupRendering } from "./core/playfield-viewport.js";
import * as d3 from "d3";
import { FlowDataHash } from "./Definations.js";

interface ViewportComponentProps {
  debugFlow?: boolean;
  data?: {
    mode: SingleGameVisualisationMode; // Adjust the type of 
    data: FlowDataHash;
  };
  theme?: string;
  width: number;
  height: number;
  axis?: string;
  players?: any[];
  settings?: any;
  playOnFocus?: boolean;
  playOnStart?: boolean;
}

export enum SingleGameVisualisationMode {
  TEAM,
  PLAYER,
  PLAYTHROUGH,
}

var engineInstance: any;
var lastDataHash: String;

export default function ViewportComponent({
  debugFlow,
  data,
  width,
  height,
  axis,
  theme,
  settings,
  players,
  playOnFocus,
  playOnStart,
}: ViewportComponentProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const viewportElement = useRef<HTMLDivElement>(null);

  const xCells = 30;
  const yCells = 24;

  const [isFreeView, setFreeView] = useState(false);
  const handleFreeViewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFreeView(event.target.checked);
  };

  const [processedData, setProcessedData] = useState<any>({});

  const [params, setParams] = useState({
    lineWidth: 0.0018,
    numSpheres: 500,
    minimalLineLength: 250,
    lineLengthRandom: 0,
    lineLengthMinimalDrop: 0,
    simTraceResolution: 0.0002,
  });

  const handleUpdateParams = (newParams: typeof params) => {
    setParams(newParams);
    if (engineInstance) {
      engineInstance.updateParams(newParams);
    }
  };

  useEffect(() => {
    if (data?.data == undefined) {
      engineInstance?.reset();
      return;
    };
    if (data.data.hash == lastDataHash) return;
    lastDataHash = data.data.hash;

    function linearInterpolation(
      point: [number, number],
      data: any[],
      key: string
    ) {
      let nearestPoints = data
        .filter(
          (p) =>
            p.passCount !== 0 &&
            p.averagePassLength !== 0 &&
            p.averagePassAngle !== 0 &&
            p.vaepScore !== 0
        )
        .sort((a, b) => {
          const distA = Math.hypot(
            a.location_X - point[0],
            a.location_Y - point[1]
          );
          const distB = Math.hypot(
            b.location_X - point[0],
            b.location_Y - point[1]
          );
          return distA - distB;
        })
        .slice(0, 4);

      const totalWeight = nearestPoints.reduce(
        (acc, p) => acc + p.passCount,
        0
      );

      return nearestPoints.reduce((acc, p: any) => {
        const weight = p.passCount / totalWeight;
        return acc + p[key] * weight;
      }, 0);
    }

    function interpolateGrid(grid: [number, number][], data: any[]) {
      return grid.map((point) => {
        const length = linearInterpolation(point, data, "averagePassLength");
        const angle = linearInterpolation(point, data, "averagePassAngle");
        const angleRad = angle * (Math.PI / 180);
        return {
          x: point[0],
          y: point[1],
          u: Math.cos(angleRad) * 3,
          v: Math.sin(angleRad) * 3,
          magnitude: length,
        };
      });
    }

    function createLinearScale(
      domain: [number, number],
      range: [number, number]
    ) {
      const [domainMin, domainMax] = domain;
      const [rangeMin, rangeMax] = range;

      return function (value: number) {
        const scale = (rangeMax - rangeMin) / (domainMax - domainMin);
        return rangeMin + (value - domainMin) * scale;
      };
    }

    function createDenseGrid(
      xMin: number,
      xMax: number,
      yMin: number,
      yMax: number,
      xCells: number,
      yCells: number
    ) {
      const x = d3.range(xMin, xMax, (xMax - xMin) / xCells);
      const y = d3.range(yMin, yMax, (yMax - yMin) / yCells);
      const grid: [number, number][] = [];

      x.forEach((xVal) => {
        y.forEach((yVal) => {
          grid.push([xVal, yVal]);
        });
      });

      return { x, y, grid };
    }

    if (data != undefined) {
      let rawData = data.data.response;
      const { x, y, grid } = createDenseGrid(0, 100, 0, 100, xCells, yCells);
      const interpolatedData = interpolateGrid(grid, rawData);

      const weights = new Array(xCells * yCells).fill(null).map(() => ({
        x: 0,
        y: 0,
        passCount: 0,
        veap: 0,
        object: null,
      }));

      const xScale = createLinearScale([0, 100], [0, width]);
      const yScale = createLinearScale([0, 100], [0, height]);

      rawData.forEach((d) => {
        const xScaled = xScale(d.location_X);
        const yScaled = yScale(d.location_Y);

        const col = Math.floor((xScaled / width) * xCells);
        const row = Math.floor((yScaled / height) * yCells);
        const index = row * xCells + col;

        weights[index] = {
          y: d.location_X,
          x: d.location_Y,
          passCount: d.passCount,
          veap: d.vaepScore,
          object: null,
        };
      });
      setProcessedData({ field: interpolatedData, weights: weights });
    }
  }, [data]);

  useEffect(() => {
    if (engineInstance) {
      engineInstance.updateFiled(processedData, playOnStart);
    }
  }, [processedData, playOnStart]);

  useEffect(() => {
    const container = canvasRef.current;
    let w = width || window.innerWidth;
    let h = height || window.innerHeight;

    if (!engineInstance) {
      engineInstance = setupRendering(
        container,
        { width: w, height: h },
        settings
      );
    } else {
      engineInstance.setSize({ width: w, height: h });
      engineInstance.updateContainer(container);
    }
    return () => {
      if (engineInstance) {
        engineInstance.dispose();
      }
    };
  }, [settings]);

  useEffect(() => {
    if (engineInstance && players) {
      engineInstance.setPlayers(players);
    }

    return () => {};
  }, [players]);

  useEffect(() => {
    switch (axis) {
      case "Pitch2D":
        engineInstance?.flatView();
        break;
      case "Pitch3D":
        engineInstance?.angleView();
        break;
    }

    return () => {};
  }, [axis]);


  useEffect(() => {
    engineInstance?.toggleTheme(theme);
  },[theme])

  return (
    <div className="sandbox">
      <div className="viewport" ref={viewportElement}>
        <div ref={canvasRef} />
      </div>
    </div>
  );
}
